"use client";
import { useState } from "react";
import Header from "../components/Header/Header";
import MediaPlayer from "../components/MediaPlayer";
import BasePic from "../assets/images/Base-pic.png";
import SiteIdentity from "../components/SiteIdentity";
import miniLogo from "../assets/images/Base-pic.png";
import { Outlet, useLocation } from "react-router-dom";
import MobileBottomNave from "../components/MobileBottomNav";
import MobileMiniPlayer from "../components/MobileMiniPlayer";
import { useHomeLayout } from "../features/layout/hooks/useHomeLayout";
import { usePersonsLoadMore } from "../features/home/hooks/usePersonsLoadMore";
import { useProjectsLoadMore } from "../features/home/hooks/useProjectsLoadMore";

export default function MainLayout() {
  const location = useLocation();
  const { data } = useHomeLayout();
  const {
    persons: loadedPersons,
    loadMore: loadMorePersons,
    loadingMore: loadingMorePersons,
    hasMore: hasMorePersons,
    isLoading: personsLoading,
  } = usePersonsLoadMore();
  const {
    projects,
    loadMore: loadMoreProjects,
    loadingMore: loadingMoreProjects,
    hasMore: hasMoreProjects,
    isLoading: projectsLoading,
  } = useProjectsLoadMore();
  const hideOnDetailPages = [
    "/institutions",
    "/managers",
    "/services",
    "/news",
  ].some((base) => location.pathname.startsWith(base));

  const [hideHeaderOnScroll, setHideHeaderOnScroll] = useState(false);
  const [, setIdentitySticky] = useState(false);
  const [globalTrackIndex, setGlobalTrackIndex] = useState<number>(0);
  const [globalIsPlaying, setGlobalIsPlaying] = useState<boolean>(false);
  const [selectedTrackId, setSelectedTrackId] = useState<number | undefined>(
    undefined,
  );
  const [isMediaCollapsed, setIsMediaCollapsed] = useState<boolean>(true);
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);
  const [tracks, setTracks] = useState<any[]>([]);
  const handleSelectTrack = (trackId: number) => {
    setSelectedTrackId(trackId);
    setGlobalIsPlaying(true);
    setIsMediaCollapsed(false);
  };

  const handleClosePlayer = () => {
    setIsMediaCollapsed(true);
  };

  const handleSetPlayerVisible = (visible: boolean) => {
    setIsMediaCollapsed(!visible);
  };

  return (
    <div className="min-h-screen bg-green-01 flex flex-col">
      {!hideHeaderOnScroll && <Header categories={data?.categories ?? []} />}

      {!hideOnDetailPages && (
        <SiteIdentity
          imageSrc={BasePic}
          compactImageSrc={miniLogo}
          className="place-self-center"
          onStickyChange={setIdentitySticky}
          enableMobileStickyOnScroll
        />
      )}

      <div className="flex-1">
        <div className="pb-28 xl:pb-0">
          <Outlet
            context={{
              setHideHeaderOnScroll,
              currentTrackId: selectedTrackId,
              isPlaying: globalIsPlaying,
              onSelectTrack: handleSelectTrack,
              onTogglePlay: () => setGlobalIsPlaying((p) => !p),
              isMediaCollapsed,
              tracks,
              setTracks,
              setIsMediaCollapsed,
              isMediaPlaying: globalIsPlaying,
              setIsMediaPlaying: setGlobalIsPlaying,
              isMobileOpen,
              setIsMobileOpen,
              loadedPersons,
              loadMorePersons,
              loadingMorePersons,
              hasMorePersons,
              personsLoading,
              projects,
              loadMoreProjects,
              loadingMoreProjects,
              hasMoreProjects,
              projectsLoading,
            }}
          />
        </div>
      </div>

      <MobileMiniPlayer
        selectedTrackId={selectedTrackId}
        isMobileOpen={!isMediaCollapsed}
        setIsMobileOpen={(open) => setIsMediaCollapsed(!open)}
        tracks={tracks}
        onTogglePlay={() => setGlobalIsPlaying((p) => !p)}
        isPlaying={globalIsPlaying}
      />

      {selectedTrackId !== undefined && (
        <div
          dir="rtl"
          className={`fixed left-0 right-0 z-40 w-full px-4 transition-all duration-300 bottom-24 xl:bottom-[30px] xl:px-6 2xl:px-11
            ${isMediaCollapsed ? "hidden opacity-0 pointer-events-none" : "block opacity-100"}
          `}
        >
          <div className="flex items-center justify-end gap-3 max-w-[1440px] mx-auto">
            <div className="w-full">
              <MediaPlayer
                onToggleCollapsed={handleClosePlayer}
                isPlaying={globalIsPlaying}
                onTogglePlay={() => setGlobalIsPlaying(!globalIsPlaying)}
                onStopPlay={() => setGlobalIsPlaying(false)}
                externalTrackIndex={globalTrackIndex}
                onExternalTrackIndexChange={(index) =>
                  setGlobalTrackIndex(index)
                }
                selectedTrackId={selectedTrackId}
                setSelectedTrackId={setSelectedTrackId}
                isMobileOpen={isMobileOpen}
                setIsMobileOpen={setIsMobileOpen}
                setIsPlayerVisible={handleSetPlayerVisible}
              />
            </div>
          </div>
        </div>
      )}

      <div className="fixed xl:hidden bottom-0 left-0 right-0 z-50">
        <div className="absolute bottom-0 left-0 right-0 h-[120px] bg-gradient-to-t from-green-04/70 to-transparent pointer-events-none" />
        <div className="relative">
          <MobileBottomNave
            categories={data?.categories ?? []}
            persons={loadedPersons}
            loadMorePersons={loadMorePersons}
            loadingMorePersons={loadingMorePersons}
            hasMorePersons={hasMorePersons}
            isPersonsLoading={personsLoading}
            projects={projects}
            loadMoreProjects={loadMoreProjects}
            loadingMoreProjects={loadingMoreProjects}
            hasMoreProjects={hasMoreProjects}
            isProjectsLoading={projectsLoading}
            extraContent={
              selectedTrackId !== undefined && isMediaCollapsed ? (
                <MobileMiniPlayer
                  selectedTrackId={selectedTrackId}
                  isMobileOpen={!isMediaCollapsed}
                  setIsMobileOpen={(open) => setIsMediaCollapsed(!open)}
                  tracks={tracks}
                  onTogglePlay={() => setGlobalIsPlaying((p) => !p)}
                  isPlaying={globalIsPlaying}
                />
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
}
